import Footer from '@oneflare/flarekit/lib/components/Footer';
import truncate from 'lodash/truncate';
import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import { isMobile } from 'react-device-detect';

import { publicRuntimeConfig } from 'lib/utils/Environment';
import HeadContent from 'shared/components/_oneflare/HeadContent/HeadContent';
import PageHeader from 'shared/components/PageHeader/PageHeader';
import { wpDataType } from 'shared/types';
import { CONTENT_HUB_WP_PATH } from 'shared/utils/constants';

import HelpCenterSearch from './components/HelpCenterSearch/HelpCenterSearch';
import {
  ArticleContentStyled,
  WinWorkGuidesStyled,
  WinWorkTitle,
  GuideHeaderStyled,
  GuideListStyled,
  GuideListItemStyled,
  GuideTitleStyled,
  GuideWrapperStyled,
  GuideContentWrapperStyled,
  GuideStyled,
  RightChevronStyled,
  FooterNavigationStyled,
  FooterNavigationTitle,
  FooterNavigationGuideLink,
  FooterNavigationNextStyled,
  FooterNavigationPrevStyled,
  BreadcrumbsStyled
} from './styled/Guide';

const propTypes = {
  pageUrl: PropTypes.string.isRequired,
  params: PropTypes.shape({
    slug: PropTypes.string
  }).isRequired,
  wpData: wpDataType.isRequired
};

const Home = ({ params, pageUrl, wpData }) => {
  const guideTitle = wpData?.guideData?.title?.rendered;
  const guideContent = wpData?.guideData?.content?.rendered;
  const guidesData = wpData?.allGuideData;
  const { metaTitle, metaDescription } = wpData?.guideData?.acf?.metaData || {};

  const determineFooterNavigation = useMemo(() => {
    let currentPageIndex = null;
    wpData?.allGuideData?.forEach((item, index) => {
      if (item.slug === params.slug) {
        currentPageIndex = index;
      }
    });

    return {
      prev: wpData?.allGuideData[currentPageIndex - 1],
      next: wpData?.allGuideData[currentPageIndex + 1]
    };
  }, [params.slug, wpData?.allGuideData]);

  const buildBreadcrumbNav = [
    {
      title: 'Oneflare',
      link: '/'
    },
    {
      title: 'Win Work Guide',
      link: '/win-work-guide'
    },
    {
      title: truncate(guideTitle, { length: 25, separator: /,? +/ })
    }
  ];

  const handleListItemClick = (link) => {
    window.location.href = link;
  };

  return (
    <>
      <HeadContent
        pageUrl={`${publicRuntimeConfig.SITE_URL}${pageUrl}`}
        pageTitle={`${metaTitle} | Oneflare`}
        metaDescription={metaDescription}
        upgradeInsecureRequests
      />
      <PageHeader />
      <GuideWrapperStyled>
        <GuideContentWrapperStyled>
          <GuideStyled>
            <BreadcrumbsStyled navigation={buildBreadcrumbNav} />
            <GuideHeaderStyled>
              <span
                dangerouslySetInnerHTML={{ __html: guideTitle }}
              />
            </GuideHeaderStyled>
            <ArticleContentStyled
              dangerouslySetInnerHTML={{ __html: guideContent }}
            />
            <FooterNavigationStyled>
              <FooterNavigationPrevStyled>
                {determineFooterNavigation.prev && (
                <>
                  <FooterNavigationTitle>Previous</FooterNavigationTitle>
                  <FooterNavigationGuideLink href={determineFooterNavigation.prev?.link.replace(CONTENT_HUB_WP_PATH, '')}>
                    {`${determineFooterNavigation.prev?.title?.rendered}`}
                  </FooterNavigationGuideLink>
                </>
                )}
              </FooterNavigationPrevStyled>
              <FooterNavigationNextStyled>
                {determineFooterNavigation.next && (
                <>
                  <FooterNavigationTitle>Next</FooterNavigationTitle>
                  <FooterNavigationGuideLink href={determineFooterNavigation.next?.link.replace(CONTENT_HUB_WP_PATH, '')}>
                    {`${determineFooterNavigation.next?.title?.rendered}`}
                  </FooterNavigationGuideLink>
                </>
                )}
              </FooterNavigationNextStyled>
            </FooterNavigationStyled>
          </GuideStyled>
          <WinWorkGuidesStyled>
            {!isMobile && (
              <>
                <WinWorkTitle>How to win work</WinWorkTitle>
                <GuideListStyled>
                  {guidesData.map(({ title, link }) => {
                    const guideLink = link.replace(CONTENT_HUB_WP_PATH, '');
                    const isCurrentPage = pageUrl === guideLink;
                    return (
                      <GuideListItemStyled
                        key={guideLink}
                        onClick={() => handleListItemClick(guideLink)}
                      >
                        <GuideTitleStyled href={guideLink} isCurrentPage={isCurrentPage}>
                          {title.rendered}
                        </GuideTitleStyled>
                        <RightChevronStyled />
                      </GuideListItemStyled>
                    );
                  })}
                </GuideListStyled>
              </>
            )}
            <HelpCenterSearch />
          </WinWorkGuidesStyled>
        </GuideContentWrapperStyled>
      </GuideWrapperStyled>
      <Footer />
    </>
  );
};

Home.propTypes = propTypes;

export default memo(Home);
